import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

const Navbar: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { name: 'Home', path: '/', icon: '🏠' },
    { name: 'About', path: '/about', icon: '✨' },
    { name: 'Collections', path: '/collections', icon: '🎨' },
    { name: 'Contact', path: '/contact', icon: '📞' },
  ];

  const isActive = (path: string) => location.pathname === path;

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 overflow-hidden ${
      isScrolled 
        ? 'bg-white/95 backdrop-blur-xl shadow-xl border-b border-lavender/20' 
        : 'bg-transparent'
    }`}>
      <div className="container-custom">
        <div className="flex items-center justify-between py-4">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3 group">
            <div className="w-12 h-12 bg-gradient-to-r from-royal-purple to-amethyst rounded-2xl flex items-center justify-center shadow-lg group-hover:shadow-xl transition-all duration-300 group-hover:scale-110 relative overflow-hidden">
              {/* Loom/Weaving Icon */}
              <svg className="w-7 h-7 text-white" fill="currentColor" viewBox="0 0 24 24">
                <path d="M3 3h18v18H3V3zm16 16V5H5v14h14zM7 7h10v2H7V7zm0 4h10v2H7v-2zm0 4h6v2H7v-2z"/>
                <path d="M9 9h2v2H9V9zm4 0h2v2h-2V9zm-4 4h2v2H9v-2zm4 0h2v2h-2v-2z"/>
              </svg>
              {/* Subtle pattern overlay */}
              <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent opacity-30"></div>
            </div>
            <span className={`text-2xl font-serif font-bold transition-colors duration-300 ${
              isScrolled 
                ? 'text-charcoal' 
                : 'text-white drop-shadow-lg'
            }`}>
              Vogueloom
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-8">
            {navItems.map((item) => (
              <Link
                key={item.name}
                to={item.path}
                className={`relative group flex items-center space-x-2 px-4 py-2 rounded-xl transition-all duration-300 ${
                  isActive(item.path)
                    ? 'text-royal-purple bg-lavender/20'
                    : isScrolled
                    ? 'text-sage hover:text-royal-purple hover:bg-lavender/10'
                    : 'text-white hover:text-lavender hover:bg-white/10'
                }`}
              >
                <span className="text-lg opacity-80 group-hover:opacity-100 transition-opacity duration-300">
                  {item.icon}
                </span>
                <span className="font-medium">{item.name}</span>
                
                {/* Active indicator */}
                {isActive(item.path) && (
                  <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-1 h-1 bg-gradient-to-r from-royal-purple to-amethyst rounded-full"></div>
                )}
              </Link>
            ))}
          </div>

          {/* CTA Button */}
          <div className="hidden lg:block">
            <Link
              to="/collections"
              className="bg-gradient-to-r from-warm-gold to-sunset-orange text-white px-6 py-3 rounded-2xl font-semibold shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 border border-warm-gold/30"
            >
              Explore Now
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className={`lg:hidden p-2 rounded-xl transition-all duration-300 ${
              isScrolled 
                ? 'text-charcoal hover:bg-lavender/10' 
                : 'text-white hover:bg-white/10'
            }`}
            aria-label="Toggle menu"
          >
            <div className="w-6 h-6 flex flex-col justify-center items-center">
              <span className={`block w-5 h-0.5 bg-current transition-all duration-300 ${
                isOpen ? 'rotate-45 translate-y-1' : '-translate-y-1'
              }`}></span>
              <span className={`block w-5 h-0.5 bg-current transition-all duration-300 ${
                isOpen ? 'opacity-0' : 'opacity-100'
              }`}></span>
              <span className={`block w-5 h-0.5 bg-current transition-all duration-300 ${
                isOpen ? '-rotate-45 -translate-y-1' : 'translate-y-1'
              }`}></span>
            </div>
          </button>
        </div>

        {/* Mobile Navigation */}
        <div className={`lg:hidden transition-all duration-500 ease-in-out ${
          isOpen 
            ? 'max-h-96 opacity-100' 
            : 'max-h-0 opacity-0'
        } overflow-hidden`}>
          <div className="py-4 space-y-2">
            {navItems.map((item) => (
              <Link
                key={item.name}
                to={item.path}
                onClick={() => setIsOpen(false)}
                className={`flex items-center space-x-3 px-4 py-3 rounded-xl transition-all duration-300 ${
                  isActive(item.path)
                    ? 'text-royal-purple bg-lavender/20'
                    : 'text-sage hover:text-royal-purple hover:bg-lavender/10'
                }`}
              >
                <span className="text-xl">{item.icon}</span>
                <span className="font-medium">{item.name}</span>
              </Link>
            ))}
            
            {/* Mobile CTA */}
            <div className="pt-4">
              <Link
                to="/collections"
                onClick={() => setIsOpen(false)}
                className="block w-full bg-gradient-to-r from-warm-gold to-sunset-orange text-white px-6 py-3 rounded-2xl font-semibold text-center shadow-lg hover:shadow-xl transition-all duration-300"
              >
                Explore Collections
              </Link>
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
